import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, DollarSign, Users, CreditCard, Plug, AlertTriangle, Activity, Bot, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function Metric({ icon: Icon, label, value, sub, testid, i = 0 }) {
  return (
    <div className="kpi-lux animate-fade-up p-4" style={{ animationDelay: `${i * 80}ms` }} data-testid={testid}>
      <div className="relative flex items-center gap-2 text-slate-400"><Icon size={15} className="text-[#8fb4ff]" /><span className="font-mono text-[10px] font-bold uppercase tracking-wider">{label}</span></div>
      <div className="relative mt-1.5 font-mono text-3xl font-black tracking-tight text-white">{value}</div>
      {sub && <div className="relative mt-0.5 text-[12px] text-slate-400">{sub}</div>}
    </div>
  );
}

export default function OwnerOverview() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/owner/overview").then(({ data }) => setD(data)).catch(() => setD(null)); }, []);
  if (!d) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-[#1a5fd0]" /></div>;

  const m = d.memberships;
  return (
    <div data-testid="owner-overview">
      <h1 className="font-head text-2xl font-extrabold text-slate-900">CEO Command Center</h1>
      <p className="mt-1 text-[13px] text-slate-500">Live company snapshot — every figure is computed from real records. As of {new Date(d.as_of).toLocaleString()}.</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric icon={DollarSign} label="MRR" value={money(d.revenue.mrr)} sub={`${money(d.revenue.arr)} ARR · ${d.revenue.active_subscriptions} active`} testid="metric-mrr" i={0} />
        <Metric icon={DollarSign} label="Revenue today" value={money(d.revenue.today)} sub={`${money(d.revenue.total)} all-time (paid)`} testid="metric-revenue-today" i={1} />
        <Metric icon={Users} label="Customers" value={d.customers.total} sub={`${d.customers.new_7d} new (7d) · ${d.customers.onboarding} onboarding`} testid="metric-customers" i={2} />
        <Metric icon={Plug} label="Providers live" value={`${d.providers.live}/${d.providers.total}`} sub="Financial data integrations" testid="metric-providers" i={3} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Owner action required */}
        <div className="lg:col-span-2">
          <h2 className="font-head text-lg font-extrabold text-slate-900">Owner action required</h2>
          <div className="mt-2 rounded-2xl border border-slate-200 bg-white p-2" data-testid="action-required">
            {d.action_required.length === 0 ? (
              <div className="flex items-center gap-2 px-3 py-6 text-[13px] text-slate-400"><CheckCircle2 size={16} className="text-emerald-500" /> Nothing needs your attention right now.</div>
            ) : d.action_required.map((a, i) => (
              <button key={i} onClick={() => navigate(a.route)} data-testid={`action-item-${i}`}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-slate-50">
                <AlertTriangle size={17} className={a.severity === "high" ? "text-red-500" : "text-amber-500"} />
                <span className="flex-1 text-[14px] font-medium text-slate-800">{a.label}</span>
                <ArrowRight size={15} className="text-slate-300" />
              </button>
            ))}
          </div>

          {/* Membership breakdown */}
          <h2 className="mt-6 font-head text-lg font-extrabold text-slate-900">Memberships</h2>
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="membership-breakdown">
            {["Preview", "Track", "Build", "Expand"].map((p) => (
              <div key={p} className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">{p}</div>
                <div className="mt-0.5 font-head text-xl font-extrabold text-slate-900">{m[p] || 0}</div>
              </div>
            ))}
          </div>
        </div>

        {/* System health + AI */}
        <div>
          <h2 className="font-head text-lg font-extrabold text-slate-900">System health</h2>
          <div className="mt-2 rounded-2xl border border-slate-200 bg-white p-3" data-testid="system-health">
            {d.system_health.map((h) => (
              <div key={h.name} className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0">
                <span className="text-[13px] text-slate-700">{h.name}{h.detail ? <span className="text-slate-400"> · {h.detail}</span> : ""}</span>
                {h.ok ? <CheckCircle2 size={16} className="text-emerald-500" /> : <XCircle size={16} className="text-red-500" />}
              </div>
            ))}
          </div>

          <h2 className="mt-6 font-head text-lg font-extrabold text-slate-900">AI workforce</h2>
          <div className="mt-2 rounded-2xl border border-slate-200 bg-white p-4" data-testid="ai-workforce">
            <div className="flex items-center gap-2 text-slate-400"><Bot size={15} /><span className="font-mono text-[10px] font-bold uppercase tracking-wider">Status</span></div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-center">
              {[["Working", d.ai_workforce.working], ["Done today", d.ai_workforce.completed_today], ["Escalated", d.ai_workforce.escalated]].map(([l, v]) => (
                <div key={l}><div className="font-head text-xl font-extrabold text-slate-900">{v}</div><div className="font-mono text-[9px] uppercase text-slate-400">{l}</div></div>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">{d.ai_workforce.note}.</p>
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-[12px] text-slate-500" data-testid="activity-today">
            <Activity size={14} /> {d.activity.analytics_events_today} tracked events today
          </div>
        </div>
      </div>
    </div>
  );
}
